import { Icon } from '@iconify/react'
import { Computed } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
// import { useEffect } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
  Label,
  PhoneInput,
  Separator,
  Textarea,
} from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import {
  Option,
} from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchCountryList } from '~in-app/shareable/country/api'
import { fetchDistributorList } from '~in-app/shareable/distributor/api'
import { fetchDistributorDivisionList } from '~in-app/shareable/division/api'
import { addHostAdminIntegrator, addIntegrator } from '~in-app/shareable/integrator/api'
import { Address, addressTypes, Contact, contactTypes, PersonsInCharge } from '~in-app/types'
import { integratorValidation } from '~in-app/validation/integrator.validation'

export const Route = createFileRoute('/console/integrator/add')({
  component: AddIntegratorPage,
  staticData: {
    privileges: ['0.3.0.1'],
  },
})

function AddIntegratorPage() {
  const router = useRouter()

  const saveIntegratorFunc = () => {
    const mutation = useMutation({
      mutationFn: addIntegrator,
      onSuccess: (response) => {
        toast.success(`Record Added Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/integrator/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveHostAdminIntegratorFunc = () => {
    const mutation = useMutation({
      mutationFn: addHostAdminIntegrator,
      onSuccess: (response) => {
        toast.success(`Record Added Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/integrator/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveIntegratorMutation = saveIntegratorFunc()
  const saveHostAdminMutation = saveHostAdminIntegratorFunc()

  const submitIntegrator = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      if (submitValue.personsInCharge.length === 0) {
        delete submitValue.personsInCharge
      }
      if (submitValue.contacts.length === 0) {
        delete submitValue.contacts
      }
      if (submitValue.address) {
        const address = submitValue.address
        const keys = Object.keys(address) as (keyof typeof address)[]
        keys.forEach((key) => {
          if (!address[key]) {
            delete address[key]
          }
        })
        if (Object.keys(address).length === 0) {
          delete submitValue.address
        }
      }
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '') {
          delete submitValue[key]
        }
      })
      if (auth$.clientId.get()) {
        saveIntegratorMutation.mutate([submitValue])
      }
      else {
        saveHostAdminMutation.mutate([submitValue])
      }
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  const formOpts = formOptions({
    defaultValues: {
      name: '',
      divisionId: '',
      clientId: auth$.clientId.get() ? auth$.clientId.get() : '',
      desp: '',
      remarks: '',
      address: {} as Address,
      contacts: [] as Contact[],
      personsInCharge: [] as PersonsInCharge[],
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: integratorValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitIntegrator(value)
      }
      catch (error) {
        console.error('Add Integrators failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })

  const formControl = useForm(formOpts)
  const queryClient = useQueryClient()
  const { data: countryList = [] } = useQuery({
    queryKey: ['add-integrator-country-listing'],
    queryFn: async () => {
      const response = await fetchCountryList()
      const countryList = response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []
      return countryList
    },
  })

  const { data: divisionList = [] } = useQuery<Option[]>({
    queryKey: ['add-integrator-division-listing'],
    queryFn: async ({ signal }) => {
      const clientId = auth$.clientId.get() ?? (formControl.getFieldValue('clientId') ?? '')
      if (!clientId || !auth$.privileges.get()?.has('0.4.0.0')) {
        return [] as Option[]
      }

      const response = await fetchDistributorDivisionList({
        clientId: clientId,
      })

      const flattenDivisionsWithReduce = (
        divisions: any[],
        parentId: string | null = null,
      ): any[] => {
        return divisions.reduce((acc: any[], division: any) => {
          acc.push({
            value: division.id,
            label: division.name,
            parentId: parentId,
          })

          if (division.mychilds && division.mychilds.length > 0) {
            acc = acc.concat(
              flattenDivisionsWithReduce(division.mychilds, division.id),
            )
          }

          return acc
        }, [])
      }

      const flatDivisionList = flattenDivisionsWithReduce(response.userDivisions)
      return flatDivisionList
    },
  })

  const { data: distributorList = [] } = useQuery({
    queryKey: ['add-integrator-distributor-listing'],
    queryFn: async ({ signal }) => {
      const clientId = auth$.clientId.get() ?? formControl.getFieldValue('clientId')
      if (clientId || !auth$.privileges.get()?.has('0.3.7.0')) {
        return
      }

      const response = await fetchDistributorList()
      const distributorList = response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []
      return distributorList
    },
  })

  const [value, setValue] = useState(['item-1', 'item-2', 'item-3', 'item-4'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">Add Integrator Page</span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1', 'item-2', 'item-3', 'item-4'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">
                      Integrator Information
                    </h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className={auth$.clientId.get() ? 'grid grid-cols-2 gap-2' : ''}>
                      <div className="grid gap-2">
                        <formControl.Field
                          name="name"
                          validators={
                            {
                              onChange: integratorValidation.shape.name,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="name"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    className="mt-1"
                                    id="name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {
                        auth$.clientId.get() && (
                          <div className="grid gap-2">
                            <formControl.Field name="divisionId">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        required
                                        className="text-xs"
                                        htmlFor="divisionId"
                                      >
                                        Division
                                      </Label>

                                      {
                                        !auth$.privileges.get()?.has('0.4.0.0')
                                        && (
                                          <span className="ml-1 text-xs text-red-500">(No Privilege)</span>
                                        )
                                      }

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              id="divisionId"
                                              label=""
                                              options={divisionList}
                                              value={field.state.value}
                                              onValueChange={
                                                (e) => {
                                                  if (e !== '') {
                                                    field.handleChange(e)
                                                    field.validate('change')
                                                  }
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>
                        )
                      }
                    </div>

                    {
                      !auth$.clientId.get() && (
                        <div className="grid grid-cols-2 gap-2">

                          <div className="grid gap-2">
                            <formControl.Field name="clientId">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        required
                                        className="text-xs"
                                        htmlFor="clientId"
                                      >
                                        Distributor
                                      </Label>

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              id="clientId"
                                              label=""
                                              options={distributorList}
                                              value={field.state.value}
                                              onValueChange={
                                                (e) => {
                                                  if (e !== '') {
                                                    field.handleChange(e)
                                                    field.validate('change')

                                                    queryClient.invalidateQueries({ queryKey: ['add-integrator-division-listing'] })
                                                  }
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>

                          <div className="grid gap-2">
                            <formControl.Field name="divisionId">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        required
                                        className="text-xs"
                                        htmlFor="divisionId"
                                      >
                                        Division
                                      </Label>

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              id="divisionId"
                                              label=""
                                              options={divisionList}
                                              value={field.state.value}
                                              onValueChange={
                                                (e) => {
                                                  if (e !== '') {
                                                    field.handleChange(e)
                                                    field.validate('change')
                                                  }
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>
                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>
                        </div>
                      )
                    }

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="desp">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="desp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="integratorRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Address</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid">
                      <formControl.Field name="address.line1">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine1"
                                >
                                  Address Line 1
                                </Label>

                                <Input
                                  id="address.line1"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                      const currentAddress = formControl.getFieldValue('address')
                                      const updatedAddress = {
                                        line1: currentAddress.line1 || '',
                                        line2: currentAddress.line2 || '',
                                        line3: currentAddress.line3 || '',
                                        countryId: currentAddress.countryId || '',
                                        type: currentAddress.type || '',
                                        postcode: currentAddress.postcode || '',
                                        state: currentAddress.state || '',
                                        city: currentAddress.city || '',
                                      }
                                      formControl.setFieldValue('address', updatedAddress)
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field name="address.line2">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine2"
                                >
                                  Address Line 2
                                </Label>

                                <Input
                                  id="address.line2"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                      const currentAddress = formControl.getFieldValue('address')
                                      const updatedAddress = {
                                        line1: currentAddress.line1 || '',
                                        line2: currentAddress.line2 || '',
                                        line3: currentAddress.line3 || '',
                                        countryId: currentAddress.countryId || '',
                                        type: currentAddress.type || '',
                                        postcode: currentAddress.postcode || '',
                                        state: currentAddress.state || '',
                                        city: currentAddress.city || '',
                                      }
                                      formControl.setFieldValue('address', updatedAddress)
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field name="address.line3">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine3"
                                >
                                  Address Line 3
                                </Label>

                                <Input
                                  id="address.line3"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                      const currentAddress = formControl.getFieldValue('address')
                                      const updatedAddress = {
                                        line1: currentAddress.line1 || '',
                                        line2: currentAddress.line2 || '',
                                        line3: currentAddress.line3 || '',
                                        countryId: currentAddress.countryId || '',
                                        type: currentAddress.type || '',
                                        postcode: currentAddress.postcode || '',
                                        state: currentAddress.state || '',
                                        city: currentAddress.city || '',
                                      }
                                      formControl.setFieldValue('address', updatedAddress)
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field name="address.postcode">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="addressPostcode"
                                  >
                                    Postcode
                                  </Label>

                                  <Input
                                    id="address.postcode"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                        const currentAddress = formControl.getFieldValue('address')
                                        const updatedAddress = {
                                          line1: currentAddress.line1 || '',
                                          line2: currentAddress.line2 || '',
                                          line3: currentAddress.line3 || '',
                                          countryId: currentAddress.countryId || '',
                                          type: currentAddress.type || '',
                                          postcode: currentAddress.postcode || '',
                                          state: currentAddress.state || '',
                                          city: currentAddress.city || '',
                                        }
                                        formControl.setFieldValue('address', updatedAddress)
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.city">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="addressCity"
                                  >
                                    City
                                  </Label>

                                  <Input
                                    id="address.city"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                        const currentAddress = formControl.getFieldValue('address')
                                        const updatedAddress = {
                                          line1: currentAddress.line1 || '',
                                          line2: currentAddress.line2 || '',
                                          line3: currentAddress.line3 || '',
                                          countryId: currentAddress.countryId || '',
                                          type: currentAddress.type || '',
                                          postcode: currentAddress.postcode || '',
                                          state: currentAddress.state || '',
                                          city: currentAddress.city || '',
                                        }
                                        formControl.setFieldValue('address', updatedAddress)
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.state">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="addressState"
                                  >
                                    State
                                  </Label>

                                  <Input
                                    id="address.state"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                        const currentAddress = formControl.getFieldValue('address')
                                        const updatedAddress = {
                                          line1: currentAddress.line1 || '',
                                          line2: currentAddress.line2 || '',
                                          line3: currentAddress.line3 || '',
                                          countryId: currentAddress.countryId || '',
                                          type: currentAddress.type || '',
                                          postcode: currentAddress.postcode || '',
                                          state: currentAddress.state || '',
                                          city: currentAddress.city || '',
                                        }
                                        formControl.setFieldValue('address', updatedAddress)
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field name="address.countryId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="addressCountry"
                                  >
                                    Country
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="address.countryId"
                                          label=""
                                          options={countryList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                                const currentAddress = formControl.getFieldValue('address')
                                                const updatedAddress = {
                                                  line1: currentAddress.line1 || '',
                                                  line2: currentAddress.line2 || '',
                                                  line3: currentAddress.line3 || '',
                                                  countryId: currentAddress.countryId || '',
                                                  type: currentAddress.type || '',
                                                  postcode: currentAddress.postcode || '',
                                                  state: currentAddress.state || '',
                                                  city: currentAddress.city || '',
                                                }
                                                formControl.setFieldValue('address', updatedAddress)
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.type">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="addressType"
                                  >
                                    Type
                                  </Label>

                                  <Dropdown
                                    id="address.type"
                                    label=""
                                    options={addressTypes}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        field.handleChange(e)
                                        field.validate('change')
                                        const currentAddress = formControl.getFieldValue('address')
                                        const updatedAddress = {
                                          line1: currentAddress.line1 || '',
                                          line2: currentAddress.line2 || '',
                                          line3: currentAddress.line3 || '',
                                          countryId: currentAddress.countryId || '',
                                          type: currentAddress.type || '',
                                          postcode: currentAddress.postcode || '',
                                          state: currentAddress.state || '',
                                          city: currentAddress.city || '',
                                        }
                                        formControl.setFieldValue('address', updatedAddress)
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Contact</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="contacts"
                    >
                      {
                        (field) => {
                          return (
                            <div className="w-full">
                              {
                                field.state.value
                                && field.state.value.length > 0 && (
                                  <div className="my-2 grid grid-cols-2 gap-2">
                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact Type
                                      </Label>
                                    </div>

                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact
                                      </Label>
                                    </div>
                                  </div>
                                )
                              }

                              {
                                field.state.value
                                && field.state.value.map((_, j) => {
                                  return (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].type`}
                                        >
                                          {
                                            (subField) => {
                                              return (
                                                <div className="relative flex flex-row items-center self-center">
                                                  <span className="mr-2">{`${j + 1}.`}</span>

                                                  <div className="relative w-full">
                                                    <Dropdown
                                                      id={`contacts[${j}].type`}
                                                      label=""
                                                      options={contactTypes}
                                                      value={subField.state.value}
                                                      onValueChange={
                                                        (e) => {
                                                          subField.handleChange(e)
                                                          subField.validate('change')
                                                          formControl.setFieldValue(`contacts[${j}].contactVal`, '')
                                                          formControl.setFieldValue(`contacts[${j}].contactLocalVal`, '')
                                                        }
                                                      }
                                                    />

                                                    {
                                                      subField.state.meta.errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].contactVal`}
                                        >
                                          {
                                            (subSubField) => {
                                              return (
                                                <>

                                                  <div className="relative w-full">
                                                    <div className="relative flex w-full flex-row items-center self-center">
                                                      {
                                                        field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                          ? (
                                                              <PhoneInput
                                                                international
                                                                className="mt-1 flex-1"
                                                                defaultCountry="MY"
                                                                id={`contacts[${j}].contactVal`}
                                                                value={subSubField.state.value}
                                                                onChange={
                                                                  (value: { fullNumber: string, localNumber: string }) => {
                                                                    subSubField.handleChange(value.fullNumber)
                                                                    formControl.setFieldValue(`contacts[${j}].contactLocalVal`, value.localNumber)
                                                                    subSubField.validate('change')
                                                                  }
                                                                }
                                                              />
                                                            )
                                                          : (
                                                              <Input
                                                                className="mt-1"
                                                                disabled={field.state.value[j].type === ''}
                                                                id={`contacts[${j}].contactVal`}
                                                                type="text"
                                                                value={
                                                                  subSubField.state.value
                                                                }
                                                                onBlur={
                                                                  subSubField.handleBlur
                                                                }
                                                                onChange={
                                                                  (e) => {
                                                                    subSubField.handleChange(
                                                                      e.target.value,
                                                                    )
                                                                    subSubField.validate(
                                                                      'change',
                                                                    )
                                                                  }
                                                                }
                                                              />
                                                            )
                                                      }

                                                      <span className="ml-2">
                                                        <IconButton
                                                          className="rounded-full"
                                                          color="muted"
                                                          type="button"
                                                          variant="text"
                                                          onClick={
                                                            () =>
                                                              field.removeValue(j)
                                                          }
                                                        >
                                                          <Icon
                                                            color="red"
                                                            icon="lucide:trash-2"
                                                          />
                                                        </IconButton>
                                                      </span>

                                                    </div>

                                                    {
                                                      subSubField.state.meta
                                                        .errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subSubField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>

                                                </>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                    </div>
                                  )
                                })
                              }

                              <Button
                                className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                color="muted"
                                type="button"
                                onClick={
                                  () =>
                                    field.pushValue({ type: '', contactVal: '', contactLocalVal: '' })
                                }
                              >
                                Add Contact
                              </Button>
                            </div>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Person In Charge</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="personsInCharge"
                    >
                      {
                        (field) => {
                          return (
                            <>
                              <Button
                                className="mt-2 rounded-md border border-slate-400  bg-transparent hover:opacity-100"
                                color="muted"
                                type="button"
                                onClick={
                                  () =>
                                    field.pushValue({
                                      firstName: '',
                                      lastName: '',
                                      contacts: [],
                                    })
                                }
                              >
                                Add person
                              </Button>

                              <div className="mt-4">
                                {
                                  field.state.value
                                  && field.state.value.map((_, i) => {
                                    return (
                                      <>
                                        <div className="gap-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-base font-semibold">
                                              {`${i + 1}.`}
                                            </div>

                                            <div className="flex-1">
                                              <span className="ml-2 mt-4 text-base font-semibold">
                                                PIC Details
                                              </span>
                                            </div>

                                            <div>
                                              <IconButton
                                                className="rounded-full"
                                                color="muted"
                                                type="button"
                                                variant="text"
                                                onClick={
                                                  () =>
                                                    field.removeValue(i)
                                                }
                                              >
                                                <Icon
                                                  color="red"
                                                  icon="lucide:trash-2"
                                                />
                                              </IconButton>
                                            </div>

                                          </div>

                                          <div className="ml-2 mt-2 w-full">
                                            <div className="grid grid-cols-2 gap-2">
                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].firstName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              required
                                                              className="text-xs"
                                                              htmlFor={`clientPersonsInCharge[${i}].firstName`}
                                                            >
                                                              First Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].firstName`}
                                                              type="text"
                                                              value={
                                                                subField.state.value
                                                              }
                                                              onBlur={
                                                                subField.handleBlur
                                                              }
                                                              onChange={
                                                                e =>
                                                                  subField.handleChange(
                                                                    e.target.value,
                                                                  )
                                                              }
                                                            />
                                                          </div>

                                                          {
                                                            subField.state.meta
                                                              .errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {
                                                                  subField.state.meta.errors.join(
                                                                    ', ',
                                                                  )
                                                                }
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>

                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].lastName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`clientPersonsInCharge[${i}].lastName`}
                                                            >
                                                              Last Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].lastName`}
                                                              type="text"
                                                              value={
                                                                subField.state.value
                                                              }
                                                              onBlur={
                                                                subField.handleBlur
                                                              }
                                                              onChange={
                                                                e =>
                                                                  subField.handleChange(
                                                                    e.target.value,
                                                                  )
                                                              }
                                                            />
                                                          </div>

                                                          {
                                                            subField.state.meta
                                                              .errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {
                                                                  subField.state.meta.errors.join(
                                                                    ', ',
                                                                  )
                                                                }
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>
                                            </div>

                                            <span className="mt-1 text-base font-semibold">
                                              PIC Contact
                                            </span>

                                            <formControl.Field
                                              mode="array"
                                              name={`personsInCharge[${i}].contacts`}
                                            >
                                              {
                                                (field) => {
                                                  return (
                                                    <div className="w-full">
                                                      {
                                                        field.state.value.length
                                                        > 0 && (
                                                          <div className="my-2 grid grid-cols-2 gap-2">
                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact Type
                                                              </Label>
                                                            </div>

                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact
                                                              </Label>
                                                            </div>
                                                          </div>
                                                        )
                                                      }

                                                      {
                                                        field.state.value
                                                        && field.state.value.map(
                                                          (_, j) => {
                                                            return (
                                                              <div className="grid grid-cols-2 gap-2">
                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={i}
                                                                    name={`personsInCharge[${i}].contacts[${j}].type`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>
                                                                            <div className="relative flex flex-row items-center self-center">
                                                                              <span className="mr-2">{`${j + 1}.`}</span>

                                                                              <div className="relative w-full">
                                                                                <Dropdown
                                                                                  id={`personsInCharge[${i}].contacts[${j}].type`}
                                                                                  label=""
                                                                                  options={
                                                                                    contactTypes
                                                                                  }
                                                                                  value={
                                                                                    subSubField
                                                                                      .state
                                                                                      .value
                                                                                  }
                                                                                  onValueChange={
                                                                                    (e) => {
                                                                                      subSubField.handleChange(
                                                                                        e,
                                                                                      )
                                                                                      formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactVal`, '')
                                                                                      formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, '')
                                                                                    }
                                                                                  }
                                                                                />

                                                                                {
                                                                                  subSubField
                                                                                    .state
                                                                                    .meta
                                                                                    .errors && (
                                                                                    <em
                                                                                      className="error-message"
                                                                                      role="alert"
                                                                                    >
                                                                                      {
                                                                                        subSubField.state.meta.errors.join(
                                                                                          ', ',
                                                                                        )
                                                                                      }
                                                                                    </em>
                                                                                  )
                                                                                }
                                                                              </div>
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={j}
                                                                    name={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>

                                                                            <div className="relative w-full">
                                                                              <div className="relative flex w-full flex-row items-center self-center">
                                                                                {
                                                                                  field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                                                    ? (
                                                                                        <PhoneInput
                                                                                          international
                                                                                          className="mt-1 flex-1"
                                                                                          defaultCountry="MY"
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          value={subSubField.state.value}
                                                                                          onChange={
                                                                                            (value: { fullNumber: string, localNumber: string }) => {
                                                                                              subSubField.handleChange(value.fullNumber)
                                                                                              formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, value.localNumber)
                                                                                              subSubField.validate('change')
                                                                                            }
                                                                                          }
                                                                                        />
                                                                                      )
                                                                                    : (
                                                                                        <Input
                                                                                          className="mt-1"
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          type="text"
                                                                                          value={subSubField.state.value}
                                                                                          onBlur={subSubField.handleBlur}
                                                                                          onChange={e => subSubField.handleChange(e.target.value)}
                                                                                        />
                                                                                      )
                                                                                }

                                                                                <span className="ml-2">
                                                                                  <IconButton
                                                                                    className="rounded-full"
                                                                                    color="muted"
                                                                                    type="button"
                                                                                    variant="text"
                                                                                    onClick={
                                                                                      () =>
                                                                                        field.removeValue(
                                                                                          j,
                                                                                        )
                                                                                    }
                                                                                  >
                                                                                    <Icon
                                                                                      color="red"
                                                                                      icon="lucide:trash-2"
                                                                                    />
                                                                                  </IconButton>
                                                                                </span>
                                                                              </div>

                                                                              {
                                                                                subSubField
                                                                                  .state
                                                                                  .meta
                                                                                  .errors && (
                                                                                  <em
                                                                                    className="error-message"
                                                                                    role="alert"
                                                                                  >
                                                                                    {
                                                                                      subSubField.state.meta.errors.join(
                                                                                        ', ',
                                                                                      )
                                                                                    }
                                                                                  </em>
                                                                                )
                                                                              }
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                              </div>
                                                            )
                                                          },
                                                        )
                                                      }

                                                      <Button
                                                        className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                                        color="muted"
                                                        type="button"
                                                        onClick={
                                                          () =>
                                                            field.pushValue({
                                                              type: '',
                                                              contactVal: '',
                                                              contactLocalVal: '',
                                                            })
                                                        }
                                                      >
                                                        Add Contact
                                                      </Button>
                                                    </div>
                                                  )
                                                }
                                              }
                                            </formControl.Field>
                                          </div>
                                        </div>

                                        {
                                          field.state.value.length !== i + 1 && (
                                            <Separator className="mb-4 mt-2" />
                                          )
                                        }
                                      </>
                                    )
                                  })
                                }
                              </div>
                            </>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!auth$.privileges.get()?.has('0.3.0.1')}
                      type="submit"
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
